/**
 * 试卷标题两行拆分：
 * 第一行 = 考评体系 + 课程分类 + 级别分类（"-"分隔的前三段）
 * 第二行 = 其余内容（试卷名称/时间戳/后缀如"举一反三"）
 * 不足三段或无剩余内容时，退化为单行显示。
 */
export interface PaperTitleParts {
  line1: string;
  line2: string;
}

/**
 * 试卷副标题拼接：副标题非空时在试卷名称后用中文括号拼上，如
 * 「2025年9月电子学会图形化编程一级真题（含9月新题型）」；
 * 副标题为空（或纯空白）时返回原名称，显示效果与无副标题完全一致。
 */
export function formatPaperTitle(
  title: string | null | undefined,
  subtitle: string | null | undefined
): string {
  const t = (title || '').trim();
  const s = (subtitle || '').trim();
  if (!s) return t;
  if (!t) return s;
  return `${t}（${s}）`;
}

/**
 * quiz 页面专用：五段口径两行拆分。
 * 第一行 = 年份 + 月份 + 课程体系 + 课程分类 + 级别分类（优先取 paper 对象的
 * year / month / organization / category_type / level_text 独立字段拼接；
 * 字段缺失时退回标题字符串按 "-" 拆前五段）。
 * 第二行 = 标题剥去第一行前缀后的剩余内容（试卷实际名称/时间戳/后缀，副标题由
 * formatPaperTitle 已拼入标题尾部，经 BlueSuffixTitle 蓝色渲染）。
 * 数据不足五段（自定义组卷/错题回顾虚拟试卷等）时返回 hasMeta=false，
 * 调用方应降级到原 splitPaperTitle 三段口径或单行，避免空行/undefined。
 */
export interface QuizPaperTitleParts extends PaperTitleParts {
  /** true 表示成功按五段口径拆出第一行 */
  hasMeta: boolean;
}

export interface PaperMetaLike {
  year?: number | string | null;
  month?: number | string | null;
  organization?: string | null;
  category_type?: string | null;
  level_text?: string | null;
}

/** 用 paper 独立字段拼第一行「年月体系分类级别」，字段不足返回 null */
function buildMetaLine(paper?: PaperMetaLike | null): string | null {
  if (!paper) return null;
  const y = paper.year != null && paper.year !== '' ? String(paper.year).replace(/[^0-9]/g, '') : '';
  const m = paper.month != null && paper.month !== '' ? String(paper.month).replace(/[^0-9]/g, '') : '';
  const yearPart = y ? `${y}年` : '';
  const monthPart = m ? `${m}月` : '';
  const org = (paper.organization || '').trim();
  const cat = (paper.category_type || '').trim();
  const level = (paper.level_text || '').trim();
  const segs = [yearPart + monthPart, org, cat, level].filter(Boolean);
  // 年月至少有一段，且组织/分类/级别至少两段，才认为元数据可用
  if (!yearPart || segs.length < 3) return null;
  return segs.join('');
}

/** 从标题字符串剥掉与 line1 等价的前缀（兼容有无分隔符的存量格式） */
function stripMetaPrefix(title: string, metaLine: string): string {
  const rest = title;
  const meta = metaLine.replace(/\s+/g, '');
  // 按元数据字符依次匹配标题前缀（忽略空白与「-」分隔符，兼容有无短横的存量格式）
  let mi = 0, ti = 0;
  while (mi < meta.length && ti < rest.length) {
    if (/\s/.test(rest[ti])) { ti++; continue; }
    if (rest[ti] === '-') { ti++; continue; }
    if (rest[ti] === meta[mi]) { ti++; mi++; continue; }
    // 容错：元数据里的「-」在标题中可能是「年/月」等字符已吸收
    if (meta[mi] === '-') { mi++; continue; }
    break;
  }
  if (mi >= meta.length) {
    return rest.slice(ti).replace(/^[\s\-—－·，,]+/, '').trim();
  }
  return rest;
}

export function splitQuizPaperTitle(
  title: string | null | undefined,
  paper?: PaperMetaLike | null
): QuizPaperTitleParts {
  const raw = (title || '').trim();
  if (!raw) return { line1: '', line2: '', hasMeta: false };

  // 优先用 paper 字段拼五段第一行：第一行固定为元数据行，
  // 第二行 = 标题剥去元数据前缀后的剩余内容；标题不含该前缀（如截图场景
  // 「2026年科学探究建模大赛E2图形化（小低组）复赛模拟题」）时第二行放完整标题
  const metaLine = buildMetaLine(paper);
  if (metaLine) {
    const stripped = stripMetaPrefix(raw, metaLine);
    if (stripped !== raw) {
      return { line1: metaLine, line2: stripped, hasMeta: true };
    }
    return { line1: metaLine, line2: raw, hasMeta: true };
  }

  // 降级：paper 字段不足时沿用原三段口径（或单行）
  const fallback = splitPaperTitle(raw);
  return { ...fallback, hasMeta: false };
}

export function splitPaperTitle(title: string | null | undefined): PaperTitleParts {
  const raw = (title || '').trim();
  if (!raw) return { line1: '', line2: '' };
  // 兼容 "A - B" 带空格分隔符
  const parts = raw.split(/\s*-\s*/).filter(Boolean);
  if (parts.length <= 3) {
    return { line1: raw, line2: '' };
  }
  return {
    line1: parts.slice(0, 3).join('-'),
    line2: parts.slice(3).join('-'),
  };
}
